// signals-web.ts — the WEB runtime's remembrance slice.
//
// 🔴 WHY THIS EXISTS: `compute_signals` was a zero stub in web-commands.ts. On web that meant two
// things, and the second is the serious one: the MemoryInsights card on the home rendered zeros
// (moments 0, span 0, milestones 0) over a book that plainly had moments in it — and the BILLED
// remembrance call was handed an empty life. Someone paying to have their pet remembered would
// have got a narration grounded in nothing. Native is unaffected (Rust computes it).
//
// Every rule below mirrors `src-tauri/src/signals.rs`. Where the two could drift, the Rust wins —
// it is the shipping surface. If you change a threshold here, change it there.
//
// The headline facts are ported in full and deliberately. They are pre-written sentences carrying
// the real figures and HER own words, and the whole point of them is that the model never invents
// a date, a count, or a memory. In this app in particular, a fabricated detail would not be a bug
// — it would be putting words in someone's mouth about a pet they have lost.

import type { Moment, Pet, RemembranceSignals } from "./store";

const MAX_MILESTONE_TITLES = 12;
const MAX_SAMPLE_MOMENTS = 14;
const MAX_PLACES = 6;

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_FULL = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

/** Mirror of signals.rs::parse — a well-formed YYYY-MM-DD only. */
function parse(d: string | null | undefined): Date | null {
  if (!d) return null;
  const head = d.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(head)) return null;
  const t = Date.parse(`${head}T00:00:00Z`);
  return Number.isNaN(t) ? null : new Date(t);
}
const ymd = (d: Date) => d.toISOString().slice(0, 10);

/** Mirror of signals.rs::excerpt — trim, cap, ellipsis. Counts CHARACTERS, as the Rust does. */
function excerpt(s: string, max: number): string {
  const t = (s ?? "").trim();
  const chars = [...t];
  return chars.length <= max ? t : chars.slice(0, max).join("") + "…";
}

/** Mirror of signals.rs::whole_years_between — drops the final year before the anniversary. */
function wholeYearsBetween(from: Date, to: Date): number {
  if (to < from) return 0;
  let years = to.getUTCFullYear() - from.getUTCFullYear();
  const a: [number, number] = [to.getUTCMonth(), to.getUTCDate()];
  const b: [number, number] = [from.getUTCMonth(), from.getUTCDate()];
  if (a[0] < b[0] || (a[0] === b[0] && a[1] < b[1])) years -= 1;
  return Math.max(0, years);
}

const displayPlace = (p: string) => { const t = (p ?? "").trim(); return t ? t[0].toUpperCase() + t.slice(1) : ""; };
const lowerFirst = (s: string) => { const t = (s ?? "").trim(); return t ? t[0].toLowerCase() + t.slice(1) : ""; };
const speciesWord = (kind: string) => (kind ?? "").trim().toLowerCase();

/** Mirror of signals.rs::pretty — "2018-03-03" → "Mar 3, 2018"; "—" when absent. */
function pretty(s: string | null | undefined): string {
  if (!s) return "—";
  const d = parse(s);
  return d ? `${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}` : s;
}

export interface FocusInput { kind: string; value: string }

/** Mirror of signals.rs::focus_matches. */
function focusMatches(fi: FocusInput, d: Date, m: Moment): boolean {
  const v = (fi.value ?? "").trim().toLowerCase();
  if (!v) return false;
  if (fi.kind === "year") return String(d.getUTCFullYear()) === v;
  if (fi.kind === "place") return (m.place ?? "").trim().toLowerCase() === v;
  if (fi.kind === "milestone") return !!m.is_milestone && (m.title ?? "").trim().toLowerCase() === v;
  return false;
}

/** Mirror of signals.rs::compute. */
export function computeSignalsWeb(
  pet: Pet | null,
  all: Moment[],
  todayStr: string,
  focusInput?: FocusInput | null,
): RemembranceSignals {
  const today = parse(todayStr) ?? new Date();

  // Well-formed dated moments only, oldest→newest for chronology.
  const dated = all
    .map((m) => ({ d: parse(m.happened_on), m }))
    .filter((x): x is { d: Date; m: Moment } => x.d !== null)
    .sort((a, b) => a.d.getTime() - b.d.getTime());

  const first = dated.length ? dated[0].d : null;
  const last = dated.length ? dated[dated.length - 1].d : null;

  // ── Pet facts ──────────────────────────────────────────────────────────────
  const name = pet?.name ?? "";
  const kind = pet?.kind ?? "";
  const birth = parse(pet?.birth_on);
  const farewell = parse(pet?.farewell_on);
  const isRemembered = farewell !== null;
  const yearsLived = birth ? wholeYearsBetween(birth, farewell ?? today) : null;
  const petFacts = {
    name, kind,
    has_birth_date: birth !== null,
    has_farewell_date: farewell !== null,
    years_lived: yearsLived,
    is_remembered: isRemembered,
  };

  // ── Overall ────────────────────────────────────────────────────────────────
  const total = dated.length;
  const milestoneCount = dated.filter((x) => x.m.is_milestone).length;
  const withPhoto = dated.filter((x) => (x.m.photo ?? "") !== "").length;
  const distinct = new Set(dated.map((x) => ymd(x.d))).size;
  const spanDays = first && last ? Math.round((last.getTime() - first.getTime()) / 86400000) + 1 : 0;
  const spanYears = first && last ? wholeYearsBetween(first, last) : 0;
  const overall = {
    total_moments: total,
    milestone_count: milestoneCount,
    ordinary_count: total - milestoneCount,
    moments_with_photo: withPhoto,
    span_years: spanYears,
    span_days: spanDays,
    first_moment_on: first ? ymd(first) : null,
    last_moment_on: last ? ymd(last) : null,
    distinct_days: distinct,
  };

  // ── Milestones — HER titles, chronological ─────────────────────────────────
  let titles = dated
    .filter((x) => x.m.is_milestone)
    .map((x) => ({
      on: ymd(x.d),
      title: (x.m.title ?? "").trim() || excerpt(x.m.caption ?? "", 60),
    }))
    .filter((t) => t.title !== "");
  if (titles.length > MAX_MILESTONE_TITLES) {
    // Keep the earliest few + the most recent few — the arc, not the middle slog.
    const head = Math.floor(MAX_MILESTONE_TITLES / 2);
    const tail = MAX_MILESTONE_TITLES - head;
    titles = [...titles.slice(0, head), ...titles.slice(titles.length - tail)];
  }
  const milestones = { count: milestoneCount, titles };

  // ── Optional focus — narrows only the SAMPLE, never the headline counts ────
  let focus: { kind: string; value: string; matched_moments: number } | null = null;
  if (focusInput && ["year", "place", "milestone"].includes(focusInput.kind)) {
    const n = dated.filter((x) => focusMatches(focusInput, x.d, x.m)).length;
    // An empty focus is no focus — narrate the whole book instead.
    if (n > 0) {
      focus = {
        kind: focusInput.kind,
        value: focusInput.kind === "place" ? displayPlace(focusInput.value) : (focusInput.value ?? "").trim(),
        matched_moments: n,
      };
    }
  }

  // ── Sampled moments — the words to narrate ────────────────────────────────
  const inScope = focus && focusInput ? dated.filter((x) => focusMatches(focusInput, x.d, x.m)) : dated;
  const toLite = (x: { d: Date; m: Moment }) => ({
    on: ymd(x.d),
    title: (x.m.title ?? "").trim(),
    caption_excerpt: excerpt(x.m.caption ?? "", 140),
    place: x.m.place ?? "",
    is_milestone: !!x.m.is_milestone,
  });
  const sampled = inScope.filter((x) => x.m.is_milestone).slice(0, MAX_SAMPLE_MOMENTS).map(toLite);
  const ordinary = inScope.filter((x) => !x.m.is_milestone);
  const room = Math.max(0, MAX_SAMPLE_MOMENTS - sampled.length);
  if (room > 0 && ordinary.length) {
    // Even stride so the sample spans the whole scope, not just its start.
    const step = Math.max(1, ordinary.length / room);
    let i = 0, taken = 0;
    while (Math.floor(i) < ordinary.length && taken < room) {
      sampled.push(toLite(ordinary[Math.floor(i)]));
      taken += 1;
      i += step;
    }
  }
  sampled.sort((a, b) => a.on.localeCompare(b.on));   // chronological for the model

  // ── Recurring places — HER labels; "recurring" means at least twice ────────
  const placeAcc = new Map<string, number>();
  for (const x of dated) {
    const p = x.m.place ?? "";
    if (p) placeAcc.set(p, (placeAcc.get(p) ?? 0) + 1);
  }
  const recurringPlaces = [...placeAcc.entries()]
    .filter(([, c]) => c >= 2)
    .map(([place, count]) => ({ place, count }))
    .sort((a, b) => b.count - a.count || a.place.localeCompare(b.place))
    .slice(0, MAX_PLACES);

  // ── Month + year spread ───────────────────────────────────────────────────
  const monthAcc = new Array(12).fill(0) as number[];
  for (const x of dated) monthAcc[x.d.getUTCMonth()] += 1;
  const monthSpread = MONTHS_SHORT.map((m, i) => ({ month: m, count: monthAcc[i] }));

  const yearAcc = new Map<number, number>();
  for (const x of dated) yearAcc.set(x.d.getUTCFullYear(), (yearAcc.get(x.d.getUTCFullYear()) ?? 0) + 1);
  const yearSpread = [...yearAcc.entries()].sort((a, b) => a[0] - b[0]).map(([year, count]) => ({ year, count }));

  const headline_facts = buildHeadlineFacts(
    petFacts, overall, milestones, recurringPlaces, monthSpread, yearSpread, focus,
  );

  // Bookends — the very first and very latest moments SHE saved, in HER own words. Two real
  // anchor points so the narration opens and closes on concrete memories, not only figures.
  if (!focus && dated.length >= 2) {
    const labelOf = (x: { d: Date; m: Moment }) => {
      const t = (x.m.title ?? "").trim();
      const body = t ? `“${t}”` : (() => { const c = excerpt(x.m.caption ?? "", 48); return c ? `“${c}”` : null; })();
      return body ? `${body} (${pretty(ymd(x.d))})` : null;
    };
    const a = labelOf(dated[0]), b = labelOf(dated[dated.length - 1]);
    if (a && b && a !== b) {
      headline_facts.push({ kind: "span", text: `It opens with ${a} and reaches ${b}.`, tone: "tender" });
    }
  }

  return {
    schema: "signals.v1",
    generated_for: todayStr,
    pet: petFacts,
    overall,
    milestones,
    sampled_moments: sampled,
    recurring_places: recurringPlaces,
    month_spread: monthSpread,
    year_spread: yearSpread,
    focus,
    headline_facts,
  } as unknown as RemembranceSignals;
}

/** Mirror of signals.rs::build_headline_facts. Wording matched exactly — see the file header. */
function buildHeadlineFacts(
  pet: { name: string; kind: string; years_lived: number | null; is_remembered: boolean },
  overall: {
    total_moments: number; milestone_count: number; ordinary_count: number; moments_with_photo: number;
    span_years: number; span_days: number; first_moment_on: string | null; last_moment_on: string | null;
  },
  milestones: { count: number; titles: { title: string }[] },
  recurringPlaces: { place: string; count: number }[],
  monthSpread: { month: string; count: number }[],
  yearSpread: { year: number; count: number }[],
  focus: { kind: string; value: string; matched_moments: number } | null,
) {
  const facts: { kind: string; text: string; tone: string }[] = [];
  if (overall.total_moments === 0) return facts;

  const who = pet.name.trim() || "them";
  const species = pet.kind.trim();

  // 0) Focus lead.
  if (focus) {
    const n = focus.matched_moments;
    const noun = n === 1 ? "moment" : "moments";
    const text = focus.kind === "year" ? `You saved ${n} ${noun} together in ${focus.value}.`
      : focus.kind === "place" ? `You saved ${n} ${noun} at ${lowerFirst(focus.value)}.`
      : `“${focus.value}” is one of the milestones you marked.`;
    facts.push({ kind: "focus", text, tone: "warm" });
  }

  // 1) The span — the whole arc, which a feed cannot show.
  if (overall.span_years >= 1) {
    const yrs = overall.span_years;
    facts.push({
      kind: "span",
      text: `Your memory book holds ${overall.total_moments} ${overall.total_moments === 1 ? "moment across" : "moments across"} ${yrs} ${yrs === 1 ? "year" : "years"}, from ${pretty(overall.first_moment_on)} to ${pretty(overall.last_moment_on)}.`,
      tone: "warm",
    });
  } else if (overall.span_days >= 1) {
    facts.push({
      kind: "span",
      text: `You've saved ${overall.total_moments} ${overall.total_moments === 1 ? "moment" : "moments"} so far, from ${pretty(overall.first_moment_on)} to ${pretty(overall.last_moment_on)}.`,
      tone: "warm",
    });
  }

  // 1b) Years lived — the life itself. Tense follows whether a farewell date is set.
  if (pet.years_lived !== null && pet.years_lived >= 1) {
    const yl = pet.years_lived;
    const label = yl === 1 ? "year" : "years";
    facts.push({
      kind: "pet",
      text: pet.is_remembered
        ? `${who} shared ${yl} ${label} of life with you.`
        : `${who} has been with you for ${yl} ${label} so far.`,
      tone: "tender",
    });
  }

  // 2) Milestones — HER marked beats, named with the first.
  if (milestones.count >= 1) {
    const t = milestones.titles[0]?.title;
    facts.push({
      kind: "milestone",
      text: milestones.count === 1 && t ? `You marked one milestone: “${t}”.`
        : t ? `You marked ${milestones.count} milestones — among them “${t}”.`
        : `You marked ${milestones.count} milestones along the way.`,
      tone: "warm",
    });
  }

  // 3) A place returned to.
  if (recurringPlaces[0]) {
    const top = recurringPlaces[0];
    facts.push({
      kind: "place",
      text: `${top.count} of your moments happened at ${lowerFirst(displayPlace(top.place))} — a place you came back to together.`,
      tone: "tender",
    });
  }

  // 4) The busiest month — only when one clearly stands out.
  const monthsWithAny = monthSpread.filter((m) => m.count > 0).length;
  let topIdx = -1;
  monthSpread.forEach((m, i) => { if (m.count > 0 && (topIdx === -1 || m.count > monthSpread[topIdx].count)) topIdx = i; });
  if (topIdx >= 0 && monthSpread[topIdx].count >= 2 && monthsWithAny >= 2) {
    facts.push({
      kind: "rhythm",
      text: `Most of your saved moments fall in ${MONTHS_FULL[topIdx]} — ${monthSpread[topIdx].count} of them.`,
      tone: "neutral",
    });
  }

  // 4b) The fullest year.
  if (yearSpread.length >= 2) {
    const top = yearSpread.reduce((a, b) => (b.count > a.count ? b : a));
    if (top.count >= 2) {
      facts.push({ kind: "rhythm", text: `${top.year} was your fullest year, with ${top.count} moments saved.`, tone: "neutral" });
    }
  }

  // 5) Ordinary days — the quiet ones.
  if (overall.ordinary_count >= 1) {
    const n = overall.ordinary_count;
    const speciesPhrase = species ? ` ${speciesWord(species)}` : "";
    facts.push({
      kind: "ordinary",
      text: `Beyond the big days, you kept ${n} ordinary ${n === 1 ? "moment" : "moments"}${speciesPhrase} — the everyday ones.`,
      tone: "tender",
    });
  }

  // 6) Photos kept.
  if (overall.moments_with_photo >= 1) {
    facts.push({
      kind: "ordinary",
      text: `${overall.moments_with_photo} of those moments have a photo saved with them.`,
      tone: "neutral",
    });
  }

  return facts;
}
